import React, { useEffect, useState } from "react";
import { Select, Spin } from "antd";
import { useTranslation } from "react-i18next";
import { useLazyGetServiceCategoryListQuery } from "features/serviceCategory/api/serviceCategoryApi";
import {
  ServiceCategory,
  ServiceCategoryFilter,
  ServiceCategorySection,
} from "features/serviceCategory/model/serviceCategory";

type Option = { value: number; label: string };

type Props = {
  value?: number | null;
  onChange?: (value: number | null) => void;
  excludeId?: number;
  /** Дочерние категории наследуют раздел родителя — предлагаем родителей только из него. */
  section?: ServiceCategorySection;
  size?: "large" | "middle" | "small";
  placeholder?: string;
  allowClear?: boolean;
};

const ServiceCategoryParentSelect: React.FC<Props> = ({
  value = null,
  onChange,
  excludeId,
  section,
  size = "middle",
  placeholder = "Не выбрано",
  allowClear = true,
}) => {
  const { i18n } = useTranslation();
  const isKy = i18n.language?.startsWith("ky");

  const [search, setSearch] = useState("");
  const [options, setOptions] = useState<Option[]>([]);
  const [fetchList, { data, isFetching }] =
    useLazyGetServiceCategoryListQuery();

  const rows: ServiceCategory[] = data?.res?.content ?? [];

  const mapRowToOption = (r: ServiceCategory): Option => ({
    value: Number(r.id),
    label: (isKy ? r.nameKy : r.nameRu) || `#${r.id}`,
  });

  useEffect(() => {
    const filter: ServiceCategoryFilter = {
      page: 1,
      size: 50,
      deleted: false,
      name: search || "",
      type: "CATEGORY",
      ...(section ? { section } : {}),
    };

    fetchList(filter);
  }, [fetchList, search, section]);

  useEffect(() => {
    const next = rows
      .filter((r) => r?.id != null)
      .filter((r) => (excludeId ? Number(r.id) !== Number(excludeId) : true))
      .map(mapRowToOption);

    setOptions(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, excludeId, isKy]);

  useEffect(() => {
    if (value == null) return;

    const has = options.some((o) => Number(o.value) === Number(value));
    if (has) return;

    const found = rows.find((r) => Number(r.id) === Number(value));
    if (found) {
      setOptions((prev) => {
        const opt = mapRowToOption(found);
        if (prev.some((p) => Number(p.value) === Number(opt.value)))
          return prev;
        return [opt, ...prev];
      });
      return;
    }

    setOptions((prev) => {
      const fallback: Option = { value: Number(value), label: `#${value}` };
      if (prev.some((p) => Number(p.value) === Number(value))) return prev;
      return [fallback, ...prev];
    });
  }, [value, options, rows]);

  const handleSearch = (q: string) => {
    setSearch(q);
  };

  return (
    <Select
      value={value ?? undefined}
      onChange={(v) => onChange?.(v ?? null)}
      allowClear={allowClear}
      showSearch
      filterOption={false}
      onSearch={handleSearch}
      placeholder={placeholder}
      loading={isFetching}
      notFoundContent={isFetching ? <Spin size="small" /> : "Ничего не найдено"}
      options={options}
      size={size}
    />
  );
};

export default ServiceCategoryParentSelect;
